import DateFnsUtils from '@date-io/date-fns';
import { MuiThemeProvider } from '@material-ui/core';
import {
    Authenticator,
    fetchAccessToken,
    getUserInfo,
    updateSessionStorage
} from '@ttc/authenticator/lib/Authenticator';
import Axios from 'axios';
import 'bootstrap/dist/css/bootstrap.css';
import { isBlank, isNotBlank } from 'components/Common/Utils/StringUtils';
import FuelVendor from 'components/FuelVendor/FuelVendor';
import AgreementTermsPopup from 'components/TermsConditions/AgreementTermsDisplay';
import { DASHBOARD_ROUTE, ERROR_PATHS } from 'constants/Constants';
import { createBrowserHistory } from 'history';
import { MuiPickersUtilsProvider } from 'material-ui-pickers';
import { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { Redirect, Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import { OidcProvider } from 'redux-oidc';
import { DEFAULT_THEME } from 'theme/CommonTheme';
import './App.css';
import TSnackbar from './components/Common/Snackbar/TSnackbar';
import { getFallBackAccessToken, setUserSession } from './components/Common/Utils/ResourceUtils';
import FuelVendorDetails from './components/FuelVendor/FuelVendorDetails/FuelVendorDetails';
import About from './components/Pages/About';
import ApplicationError from './components/Pages/ApplicationError';
import NotFound from './components/Pages/NotFound';
import VisibilitySharingDetail from './components/VisibilitySharing/VisibilitySharingDetail';
import VisibilitySharingInbox from './components/VisibilitySharing/VisibilitySharingInbox';
import {
    isLocalReportRequest,
    isSharingAgreementsReportRequest
} from './components/SharingAgreementsReport/reportEndpoints';
import AgreementTermsMapping from './components/TermsConditions/AgreementTermsMapping';
import AgreementsReport from './components/TermsConditions/AgreementsReport';
import AgreementsContainer from './components/TrustCenter/AgreementsContainer/AgreementsContainer';
import CarrierConfigPage from './components/TrustCenter/ProviderDetails/Agreement/CarrierConfig/CarrierConfigPage';
import ProviderDetail from './components/TrustCenter/ProviderDetails/ProviderDetail';
import TrustCenter from './components/TrustCenter/TrustCenter';
import Spinner from './components/UI/Spinner/Spinner';
import NoApplicationAccess from './components/signin/DeletedUser/NoApplicationAccess';
import RevokedUser from './components/signin/RevokedUser/RevokedUser';
import UnAuthorizedUser from './components/signin/UnAuthorizedUser/UnAuthorizedUser';
import authenticationManager from './container/AuthenticationManager';
import automationAuthenticationManager from './container/AutomationAuthenticationManager';
import Dashboard from './container/Dashboard';
import { SHOW_DANGER_BAR } from './store/actions/types';
import store from './store/configure';

let history = createBrowserHistory();

Axios.interceptors.request.use(
    (config) => {
        // Local report overrides hit APIs directly (no Kong). Bearer OIDC tokens fail V1 locally.
        if (isSharingAgreementsReportRequest(config) && isLocalReportRequest(config)) {
            return config;
        }
        // perform a task before the request is sent
        if (isBlank(config.headers['accountId'])) {
            const accountId = store.getState().session.sessionValue.accountId;
            config.headers['accountId'] = accountId; // TODO: check and remove this if not needed
        }
        config.headers['Authorization'] = 'Bearer ' + getFallBackAccessToken();

        return config;
    },
    (error) => {
        // handle the error
        return Promise.reject(error);
    }
);

Axios.interceptors.response.use(
    (response) => {
        // do something with the response data

        return response;
    },
    (error) => {
        if (isSharingAgreementsReportRequest(error.config)) {
            return Promise.reject(error);
        }
        // handle the response error
        if (error.response) {
            console.error('[API error]', {
                status: error.response.status,
                method: error.config?.method,
                url: error.config?.url,
                data: error.response.data
            });
        }
        if (error.response && error.response.status === 401 && error.config.method === 'get') {
            window.location = ` ${window.location.origin}/unauthorized-access`;
        } else if (error.response && error.response.status === 401) {
            store.dispatch({
                type: SHOW_DANGER_BAR,
                payload: 'Your access is restricted for this operation'
            });
        }
        return Promise.reject(error);
    }
);

const App = (props) => {
    const [allowLoad, setAllowLoad] = useState(false);
    const [infoLoaded, setInfoLoaded] = useState({});
    let urlPath = '';

    useEffect(() => {
        const path = ERROR_PATHS.some((errorPath) => window.location.href.includes(errorPath));
        const code = new URLSearchParams(window.location.search).get('code');
        const urlAccountId = new URLSearchParams(window.location.search).get('accountId');
        let access_token = sessionStorage.getItem('access_token');

        if (isNotBlank(urlAccountId)) {
            sessionStorage.setItem('urlAccountId', urlAccountId);
        }
        if (isBlank(code) && isBlank(sessionStorage.getItem('access_token'))) {
            urlPath = window.location.pathname + window.location.search;
            sessionStorage.setItem('pathname', urlPath);
            history.push('/');
        } else if (isNotBlank(sessionStorage.getItem('access_token')) && isBlank(urlAccountId)) {
            sessionStorage.removeItem('urlAccountId');
        }
        if (!path && isNotBlank(access_token)) {
            getUserInfo(authenticationManager).then((res) => {
                return setInfoLoaded(res);
            });
        } else {
            setAllowLoad(true);
        }
    }, [props]);

    useEffect(() => {
        infoLoaded && setUserSession(infoLoaded, setAllowLoad);
    }, [infoLoaded]);

    useEffect(() => {
        updateSessionStorage(authenticationManager);
    }, []);

    let isAutomationLogin = isNotBlank(sessionStorage.getItem('identity_provider'));

    return (
        <Provider store={store}>
            <OidcProvider
                store={store}
                userManager={
                    isAutomationLogin ? automationAuthenticationManager : authenticationManager
                }
            >
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <MuiThemeProvider theme={DEFAULT_THEME}>
                        {allowLoad ? (
                            <Router>
                                <div className="containerBody Mui-Table-default">
                                    <AgreementTermsPopup />
                                    <Switch>
                                        <Route
                                            exact
                                            path={'/'}
                                            component={(props) =>
                                                Authenticator(
                                                    props,
                                                    isAutomationLogin
                                                        ? automationAuthenticationManager
                                                        : authenticationManager,
                                                    DASHBOARD_ROUTE
                                                )
                                            }
                                        />
                                        <Route
                                            exact
                                            path="/ui/signin"
                                            component={(props) =>
                                                fetchAccessToken(
                                                    props,
                                                    isAutomationLogin
                                                        ? automationAuthenticationManager
                                                        : authenticationManager,
                                                    urlPath
                                                )
                                            }
                                        />

                                        <Route exact path="/about" component={About} />
                                        <Route exact path="/dashboard" component={Dashboard} />
                                        <Route
                                            exact
                                            path="/agreement-terms"
                                            component={AgreementTermsMapping}
                                        />
                                        <Route
                                            exact
                                            path="/agreement-report/:id"
                                            component={AgreementsReport}
                                        />
                                        <Route
                                            exact
                                            path="/sharing-agreements"
                                            component={VisibilitySharingInbox}
                                        />
                                        <Route
                                            exact
                                            path="/sharing-agreements/:id"
                                            component={VisibilitySharingDetail}
                                        />
                                        <Redirect
                                            exact
                                            from="/sharing-agreements-v2"
                                            to="/sharing-agreements"
                                        />
                                        <Redirect
                                            exact
                                            from="/sharing-agreements-report"
                                            to="/sharing-agreements"
                                        />
                        <Route
                            exact
                            path="/sharingagreements-detail/:id"
                            render={({ match }) => (
                                <Redirect
                                    to={{
                                        pathname: `/sharing-agreements/${match.params.id}`,
                                        search: '?source=v1'
                                    }}
                                />
                            )}
                        />
                                        <Route
                                            exact
                                            path="/connections/:id/:name/agreements"
                                            component={ProviderDetail}
                                        />
                                        <Route
                                            exact
                                            path="/provider-summary/v2/agreements"
                                            component={ProviderDetail}
                                        />
                                        <Route
                                            exact
                                            path="/provider-summary/v2/agreements/:sharingAgreementId"
                                            component={ProviderDetail}
                                        />
                                        <Route
                                            exact
                                            path="/provider/:providerId/create-connection/:type"
                                            component={ProviderDetail}
                                        />
                                        <Route
                                            exact
                                            path="/provider/:providerAccountId/create-connection"
                                            component={ProviderDetail}
                                        />
                                        <Route
                                            exact
                                            path="/agreements/:type/:id/:activity"
                                            component={AgreementsContainer}
                                        />
                                        <Route
                                            exact
                                            path="/connections/v2/provider/:providerAccountId/carrier-config"
                                            component={CarrierConfigPage}
                                        />
                                        <Route
                                            exact
                                            path="/connections/v2/provider/:providerAccountId/create"
                                            component={ProviderDetail}
                                        />
                                        <Route
                                            exact
                                            path="/connections/v2/agreements/:sharingAgreementId"
                                            component={ProviderDetail}
                                        />
                                        <Route
                                            exact
                                            path="/connections/v2/provider/:providerAccountId"
                                            component={TrustCenter}
                                        />
                                        <Route
                                            exact
                                            path="/connections/v2"
                                            component={TrustCenter}
                                        />
                                        <Route
                                            exact
                                            path="/connections/provider/:providerAccountId"
                                            component={TrustCenter}
                                        />
                                        <Route exact path="/connections" component={TrustCenter} />
                                        <Route
                                            exact
                                            path="/unauthorized-access"
                                            component={UnAuthorizedUser}
                                        />
                                        <Route
                                            exact
                                            path="/restricted-application-access"
                                            component={NoApplicationAccess}
                                        />
                                        <Route
                                            exact
                                            path="/restricted-access"
                                            component={RevokedUser}
                                        />
                                        <Route
                                            exact
                                            path="/application-error"
                                            component={ApplicationError}
                                        />

                                        <Route
                                            exact
                                            path="/fuelcardpartners"
                                            component={FuelVendor}
                                        />
                                        <Route
                                            exact
                                            path="/fuelcard-details"
                                            component={FuelVendorDetails}
                                        />
                                        <Route
                                            exact
                                            path="/fuelcard-details/:id"
                                            component={FuelVendorDetails}
                                        />
                                        <Route
                                            exact
                                            path="/fuelcard-details/:id/:mode"
                                            component={FuelVendorDetails}
                                        />
                                        <Route component={NotFound} />
                                    </Switch>
                                    <TSnackbar />
                                </div>
                            </Router>
                        ) : (
                            <Spinner />
                        )}
                    </MuiThemeProvider>
                </MuiPickersUtilsProvider>
            </OidcProvider>
        </Provider>
    );
};

export default App;
